import { useEffect, useRef, useState } from "react";
import { AdminLayout } from "../../components/Layouts";
import { api, readUser, fmt, errMsg, imgSrc } from "../../lib/api";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";

const EMPTY = { name: "", price: 100, days: 30, daily_production: 1.5, description: "", active: true, sort_order: 1, image_url: "" };

export default function AdminPackages() {
  const user = readUser();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function load() { const r = await api.get("/admin/packages"); setRows(r.data); }
  useEffect(() => { load().catch(() => {}); }, []);

  async function submit(e) {
    e.preventDefault();
    setNotice("");
    try {
      const payload = { ...form, price: Number(form.price), days: Number(form.days), daily_production: Number(form.daily_production), sort_order: Number(form.sort_order) };
      if (editing) await api.put(`/admin/packages/${editing}`, payload);
      else await api.post("/admin/packages", payload);
      setForm(EMPTY); setEditing(null); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { setNotice(errMsg(e)); }
  }
  async function remove(id) {
    if (!window.confirm("Paketi silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/packages/${id}`); load();
  }
  function edit(p) {
    setEditing(p.id);
    setForm({ name: p.name, price: p.price, days: p.days, daily_production: p.daily_production, description: p.description || "", active: p.active, sort_order: p.sort_order || 1, image_url: p.image_url || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setNotice("Görsel 5MB'den küçük olmalı."); return; }
    setUploading(true);
    setNotice("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/packages/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: r.data.image_url }));
    } catch (err) {
      setNotice(errMsg(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function clearImage() {
    setForm((prev) => ({ ...prev, image_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <AdminLayout user={user} active="Paketler">
      <section className="admin-panel">
        <div className="admin-panel-title"><div><span className="eyebrow">PAKET YÖNETİMİ</span><h2>{editing ? "Paket Düzenle" : "Yeni Paket Ekle"}</h2></div>{editing && <button className="btn btn-light" onClick={() => { setEditing(null); setForm(EMPTY); clearImage(); }} data-testid="admin-pkg-cancel">İptal</button>}</div>
        {notice && <div className="error">{notice}</div>}
        <form onSubmit={submit} className="pkg-form">
          <label>Paket Adı<input data-testid="pkg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          <label>Fiyat (USDT)<input data-testid="pkg-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required /></label>
          <label>Gün Sayısı<input data-testid="pkg-days" type="number" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} required /></label>
          <label>Günlük Üretim<input data-testid="pkg-daily" type="number" step="0.01" value={form.daily_production} onChange={(e) => setForm({ ...form, daily_production: e.target.value })} required /></label>
          <label>Sıralama<input data-testid="pkg-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></label>
          <label className="full-row">Açıklama<input data-testid="pkg-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>

          <div className="full-row pkg-image-field">
            <div className="pkg-image-label">
              <span>Paket Görseli</span>
              <small>JPG · PNG · WEBP · GIF — max 5MB</small>
            </div>
            <div className="pkg-image-row">
              <div className="pkg-image-preview" data-testid="pkg-image-preview">
                {form.image_url ? (
                  <img src={imgSrc(form.image_url)} alt="Paket görseli" />
                ) : (
                  <div className="pkg-image-empty"><ImageIcon size={22} /><small>Görsel yüklenmedi</small></div>
                )}
              </div>
              <div className="pkg-image-actions">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onPickFile} style={{ display: "none" }} data-testid="pkg-image-input" />
                <button type="button" className="btn btn-light" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="pkg-image-upload-btn">
                  {uploading ? <><Loader2 size={14} className="spin" /> Yükleniyor…</> : <><Upload size={14} /> {form.image_url ? "Değiştir" : "Görsel Yükle"}</>}
                </button>
                {form.image_url && (
                  <button type="button" className="btn btn-ghost" onClick={clearImage} data-testid="pkg-image-clear">
                    <X size={14} /> Kaldır
                  </button>
                )}
              </div>
            </div>
          </div>

          <label className="checkbox"><input data-testid="pkg-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif</label>
          <button className="btn btn-accent" data-testid="pkg-submit" disabled={uploading}>{editing ? "Kaydet" : "Oluştur"}</button>
        </form>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-title"><div><span className="eyebrow">LİSTE</span><h2>Paketler</h2></div></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Görsel</th><th>Ad</th><th>Fiyat</th><th>Gün</th><th>Günlük</th><th>Dönem Sonu</th><th>Sıralama</th><th>Durum</th><th>İşlem</th></tr></thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={p.id} data-testid={`admin-pkg-${i}`}>
                  <td>
                    {p.image_url ? (
                      <img src={imgSrc(p.image_url)} alt={p.name} className="pkg-thumb" data-testid={`admin-pkg-thumb-${i}`} />
                    ) : (
                      <div className="pkg-thumb pkg-thumb-empty"><ImageIcon size={16} /></div>
                    )}
                  </td>
                  <td>{p.name}</td>
                  <td>${fmt(p.price)}</td>
                  <td>{p.days}</td>
                  <td>${fmt(p.daily_production)}</td>
                  <td>${fmt(p.end_of_term)}</td>
                  <td>{p.sort_order}</td>
                  <td><span className={`status ${p.active ? "confirmed" : "rejected"}`}>{p.active ? "Aktif" : "Pasif"}</span></td>
                  <td><button className="table-action approve" data-testid={`admin-pkg-edit-${i}`} onClick={() => edit(p)}>Düzenle</button><button className="table-action reject" data-testid={`admin-pkg-delete-${i}`} onClick={() => remove(p.id)}>Sil</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}
